import { useMemo } from 'react';
import type { Task, TaskStatus, FilterState } from '@/types';

export interface TaskStats {
  total: number;
  byStatus: Record<TaskStatus, number>;
  completionRate: number;
  rootCount: number;
  maxDepth: number;
}

export interface UseTaskTreeResult {
  rootTasks: Task[];
  childrenMap: Map<string, Task[]>;
  filteredCount: number;
  stats: TaskStats;
  hasAnyChildren: (id: string) => boolean;
}

/** Normalize a string: lowercase + strip accents/diacritics. */
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function useTaskTree(
  tasks: Task[],
  filters: FilterState,
): UseTaskTreeResult {
  return useMemo(() => {
    const { ownerIds, statuses, search, depthScope } = filters;

    // ── 1. Filter tasks ────────────────────────────────────────────────────
    const ownerSet = new Set(ownerIds.map(normalize));
    const statusSet = new Set(statuses);
    const searchNorm = search ? normalize(search) : '';

    const filtered = tasks.filter((task) => {
      // ownerIds: keep only if in set (no-op when filter is empty)
      if (ownerIds.length > 0 && !ownerSet.has(normalize(task.ownerId))) return false;

      // statuses: keep only if in set (no-op when filter is empty)
      if (statuses.length > 0 && !statusSet.has(task.status)) return false;

      // search: case-insensitive + accent-insensitive match on title and description
      if (searchNorm) {
        const inTitle = normalize(task.title).includes(searchNorm);
        const inDesc = normalize(task.description ?? '').includes(searchNorm);
        if (!inTitle && !inDesc) return false;
      }

      // depthScope — use truthy check for parentId (handles "", null, undefined)
      if (depthScope === 'root') {
        if (task.parentId) return false; // has a parent → not a root
      } else if (depthScope === 'subtasks') {
        if (!task.parentId) return false; // no parent → is a root, skip it
      }
      // 'all' → no depth filter

      return true;
    });

    // ── 2. Build children map ──────────────────────────────────────────────
    const filteredIds = new Set(filtered.map((t) => t._id));
    const childrenMap = new Map<string, Task[]>();

    for (const task of filtered) {
      if (task.parentId) {
        let siblings = childrenMap.get(task.parentId);
        if (!siblings) {
          siblings = [];
          childrenMap.set(task.parentId, siblings);
        }
        siblings.push(task);
      }
    }

    // Sort children by createdAt ascending
    for (const [, siblings] of childrenMap) {
      siblings.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }

    // ── 3. Determine root tasks ────────────────────────────────────────────
    // A task is a root if it has no parentId (truthy check), or its parent was filtered out.
    const rootTasks = filtered.filter(
      (task) => !task.parentId || !filteredIds.has(task.parentId),
    );

    // Sort root tasks by createdAt descending
    rootTasks.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

    // ── 4. Compute statistics ──────────────────────────────────────────────
    const byStatus: Record<TaskStatus, number> = {
      pending: 0,
      in_progress: 0,
      blocked: 0,
      completed: 0,
      cancelled: 0,
    };

    let rootCount = 0;
    let maxDepth = 0;

    for (const task of filtered) {
      byStatus[task.status]++;
      if (task.depth === 0) rootCount++;
      if (task.depth > maxDepth) maxDepth = task.depth;
    }

    const total = filtered.length;
    const completionRate = total === 0 ? 0 : (byStatus.completed / total) * 100;

    const stats: TaskStats = {
      total,
      byStatus,
      completionRate,
      rootCount,
      maxDepth,
    };

    // ── 5. Helper ──────────────────────────────────────────────────────────
    const hasAnyChildren = (id: string): boolean => {
      const children = childrenMap.get(id);
      return children !== undefined && children.length > 0;
    };

    return {
      rootTasks,
      childrenMap,
      filteredCount: total,
      stats,
      hasAnyChildren,
    };
  }, [tasks, filters.ownerIds, filters.statuses, filters.search, filters.depthScope]);
}
